import React, { useState, useEffect } from 'react'
import { Mail, Send, Clock, CheckCircle, XCircle, AlertCircle, Eye, Calendar, Users, MapPin, SkipForward, TrendingUp, CreditCard as Edit, X, CreditCard, MessageSquare, MessageCircle, Filter, RotateCcw, Package, Star } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { SuppliesManagement } from './SuppliesManagement'
import { EnvoisPreSession } from './EnvoisPreSession'
import { EvaluationCampaign } from './EvaluationCampaign'
import { CommunautesWhatsapp } from './CommunautesWhatsapp'
import { getAuthHeader } from '../../../lib/edgeFunctions';

interface EmailLog {
  id: string
  created_at: string
  recipient_email: string
  subject: string
  status: 'success' | 'failed' | 'skipped_duplicate' | 'batch_completed' | 'batch_partial_failure'
  error_message?: string
  session_id?: string
  total_recipients_in_batch?: number
  successful_sends_in_batch?: number
  failed_sends_in_batch?: number
  skipped_duplicates_in_batch?: number
  type?: 'pre_session' | 'custom_group' | 'payment_reminder' | 'evaluation' | 'whatsapp_invitation'
}

interface CurrentWeekSession {
  session_id: string
  session_start_date: string
  session_end_date: string
  session_periode: string
  session_semaine: string
  center_name: string
  center_id: string
  stage_title: string
  registrations_count: number
}

interface SessionFilters {
  periode: string
  center_id: string
  semaine: string
}

interface FilterOption {
  value: string
  label: string
}

type CommunicationTab = 'pre-session' | 'group-messages' | 'whatsapp' | 'payment-reminders' | 'evaluation' | 'history' | 'supplies'

export function CommunicationManagement() {
  const [activeTab, setActiveTab] = useState<CommunicationTab>('pre-session')
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([])
  const [currentWeekSessions, setCurrentWeekSessions] = useState<CurrentWeekSession[]>([])
  const [allSessions, setAllSessions] = useState<CurrentWeekSession[]>([])
  const [filteredSessions, setFilteredSessions] = useState<CurrentWeekSession[]>([])
  const [showFilters, setShowFilters] = useState(false)
  const [sessionFilters, setSessionFilters] = useState<SessionFilters>({
    periode: '',
    center_id: '',
    semaine: ''
  })
  const [filterOptions, setFilterOptions] = useState<{
    periodes: FilterOption[]
    centers: FilterOption[]
    semaines: FilterOption[]
  }>({
    periodes: [],
    centers: [],
    semaines: []
  })
  const [uniqueCenters, setUniqueCenters] = useState<{id: string, name: string}[]>([])
  const [loading, setLoading] = useState(true)
  
  // États pour le modal d'email personnalisé
  const [showCustomEmailModal, setShowCustomEmailModal] = useState(false)
  const [selectedSessionForCustomEmail, setSelectedSessionForCustomEmail] = useState<CurrentWeekSession | null>(null)
  const [customEmailSubject, setCustomEmailSubject] = useState('')
  const [customEmailBody, setCustomEmailBody] = useState('')
  const [sendingCustomEmail, setSendingCustomEmail] = useState(false)

  useEffect(() => {
    fetchEmailLogs()
    fetchAllSessions()
    fetchCentersFromTable()
  }, [])

  useEffect(() => {
    applySessionFilters()
  }, [allSessions, sessionFilters])

  const fetchEmailLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('email_send_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) throw error
      setEmailLogs(data || [])
    } catch (err) {
      console.error('Error fetching email logs:', err)
    }
  }

  const fetchCurrentWeekSessions = async () => {
    try {
      // Récupérer les sessions qui sont en cours cette semaine
      const today = new Date()
      const todayStr = today.toISOString().split('T')[0]

      // Récupérer les sessions où aujourd'hui est entre start_date et end_date
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          id,
          start_date,
          end_date,
          periode,
          semaine,
          center_id,
          center:centers(name),
          stage:stages(title),
          registrations!activity_id(
            id,
            payment_status
          )
        `)
        .lte('start_date', todayStr)
        .gte('end_date', todayStr)
        .eq('active', true)
        .order('start_date', { ascending: true })

      if (error) throw error

      const formattedSessions = data?.map(session => {
        // Compter seulement les inscriptions non annulées
        const activeRegistrations = session.registrations?.filter(
          reg => reg.payment_status !== 'cancelled'
        ).length || 0

        return {
          session_id: session.id,
          session_start_date: session.start_date,
          session_end_date: session.end_date,
          session_periode: session.periode,
          session_semaine: session.semaine,
          center_id: session.center_id,
          center_name: session.center?.name || 'Centre inconnu',
          stage_title: session.stage?.title || 'Stage inconnu',
          registrations_count: activeRegistrations
        }
      }).filter(session => session.registrations_count > 0) || []

      setCurrentWeekSessions(formattedSessions)
    } catch (err) {
      console.error('Error fetching current week sessions:', err)
    }
  }

  const fetchAllSessions = async () => {
    try {
      // Récupérer toutes les sessions des 4 dernières semaines et 4 prochaines semaines
      const today = new Date()
      const fourWeeksAgo = new Date()
      fourWeeksAgo.setDate(today.getDate() - 28)
      const fourWeeksLater = new Date()
      fourWeeksLater.setDate(today.getDate() + 28)
      
      const startDateStr = fourWeeksAgo.toISOString().split('T')[0]
      const endDateStr = fourWeeksLater.toISOString().split('T')[0]

      const { data, error } = await supabase
        .from('sessions')
        .select(`
          id,
          start_date,
          end_date,
          periode,
          semaine,
          center_id,
          center:centers(id, name),
          stage:stages(title),
          registrations!activity_id(
            id,
            payment_status
          )
        `)
        .gte('start_date', startDateStr)
        .lte('start_date', endDateStr)
        .eq('active', true)
        .order('start_date', { ascending: false })

      if (error) throw error

      const formattedSessions = data?.map(session => {
        // Compter seulement les inscriptions non annulées
        const activeRegistrations = session.registrations?.filter(
          reg => reg.payment_status !== 'cancelled'
        ).length || 0

        return {
          session_id: session.id,
          session_start_date: session.start_date,
          session_end_date: session.end_date,
          session_periode: session.periode,
          session_semaine: session.semaine,
          center_id: session.center_id,
          center_name: session.center?.name || 'Centre inconnu',
          stage_title: session.stage?.title || 'Stage inconnu',
          registrations_count: activeRegistrations
        }
      }).filter(session => session.registrations_count > 0) || []

      setAllSessions(formattedSessions)

      // Extraire les options de filtrage
      const periodes = [...new Set(formattedSessions.map(s => s.session_periode))]
        .map(p => ({ value: p, label: p }))
        .sort((a, b) => a.label.localeCompare(b.label))

      const centers = [...new Set(formattedSessions.map(s => ({ id: s.center_id, name: s.center_name })))]
        .map(c => ({ value: c.id, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label))

      // Une journee pedagogique n'a pas de semaine : `null` ferait planter le tri et vider les filtres
      const semaines = [...new Set(formattedSessions.map(s => s.session_semaine))]
        .filter((s): s is string => Boolean(s))
        .map(s => ({ value: s, label: s }))
        .sort((a, b) => a.label.localeCompare(b.label))

      setFilterOptions({
        periodes,
        centers,
        semaines
      })

      // Par défaut, afficher les sessions en cours cette semaine
      const currentDate = new Date()
      const todayStr = currentDate.toISOString().split('T')[0]
      const currentWeekSessions = formattedSessions.filter(session => 
        session.session_start_date <= todayStr && session.session_end_date >= todayStr
      )
      setCurrentWeekSessions(currentWeekSessions)
      setFilteredSessions(currentWeekSessions)

    } catch (err) {
      console.error('Error fetching all sessions:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchCentersFromTable = async () => {
    try {
      const { data, error } = await supabase
        .from('centers')
        .select('id, name')
        .eq('active', true)
        .order('name')

      if (error) throw error
      setUniqueCenters(data || [])
    } catch (err) {
      console.error('Error fetching centers:', err)
    }
  }

  const applySessionFilters = () => {
    let filtered = allSessions

    // Si aucun filtre n'est appliqué, montrer les sessions en cours cette semaine
    const hasActiveFilters = sessionFilters.periode || sessionFilters.center_id || sessionFilters.semaine
    
    if (!hasActiveFilters) {
      const today = new Date()
      const todayStr = today.toISOString().split('T')[0]
      filtered = allSessions.filter(session => 
        session.session_start_date <= todayStr && session.session_end_date >= todayStr
      )
    } else {
      // Appliquer les filtres
      if (sessionFilters.periode) {
        filtered = filtered.filter(session => session.session_periode === sessionFilters.periode)
      }
      if (sessionFilters.center_id) {
        filtered = filtered.filter(session => session.center_id === sessionFilters.center_id)
      }
      if (sessionFilters.semaine) {
        filtered = filtered.filter(session => session.session_semaine === sessionFilters.semaine)
      }
    }

    setFilteredSessions(filtered)
  }

  const resetSessionFilters = () => {
    setSessionFilters({
      periode: '',
      center_id: '',
      semaine: ''
    })
    setShowFilters(false)
  }

  const getSessionStatus = (session: CurrentWeekSession) => {
    const today = new Date()
    const todayStr = today.toISOString().split('T')[0]
    const startDate = session.session_start_date
    const endDate = session.session_end_date

    if (startDate <= todayStr && endDate >= todayStr) {
      return { status: 'current', label: 'En cours', color: 'bg-green-100 text-green-800' }
    } else if (startDate > todayStr) {
      return { status: 'future', label: 'À venir', color: 'bg-blue-100 text-blue-800' }
    } else {
      return { status: 'past', label: 'Terminée', color: 'bg-gray-100 text-gray-800' }
    }
  }

  const sendCustomEmailForSession = async () => {
    if (!selectedSessionForCustomEmail || !customEmailSubject.trim() || !customEmailBody.trim()) {
      alert('Veuillez remplir tous les champs obligatoires.')
      return
    }

    setSendingCustomEmail(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-custom-group-email`, {
        method: 'POST',
        headers: {
          'Authorization': await getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: selectedSessionForCustomEmail.session_id,
          subject: customEmailSubject,
          body: customEmailBody
        })
      })

      const result = await response.json()
      
      if (response.ok) {
        const summaryText = [
          `Total: ${result.total_recipients}`,
          `Succès: ${result.success_count}`,
          `Échecs: ${result.failure_count}`,
          result.skipped_duplicates_count > 0 ? `Doublons ignorés: ${result.skipped_duplicates_count}` : null
        ].filter(Boolean).join('\n- ')

        alert(`Email personnalisé envoyé avec succès!\n\nRésumé:\n- ${summaryText}`)
        
        // Fermer le modal et réinitialiser les champs
        setShowCustomEmailModal(false)
        setSelectedSessionForCustomEmail(null)
        setCustomEmailSubject('')
        setCustomEmailBody('')
        
        await fetchEmailLogs() // Rafraîchir les logs
      } else {
        throw new Error(result.error || 'Erreur lors de l\'envoi')
      }
    } catch (err) {
      console.error('Error sending custom email:', err)
      alert(`Erreur lors de l'envoi de l'email personnalisé: ${err instanceof Error ? err.message : 'Erreur inconnue'}`)
    } finally {
      setSendingCustomEmail(false)
    }
  }

  const openCustomEmailModal = (session: CurrentWeekSession) => {
    setSelectedSessionForCustomEmail(session)
    setCustomEmailSubject(`Information importante - ${session.stage_title}`)
    setCustomEmailBody(`Bonjour {nom_parent},

Nous vous écrivons concernant le stage {stage} de votre enfant {enfants} au {centre}.

[Votre message ici]

Variables disponibles :
- {nom_parent} : Nom du parent
- {enfants} : Nom(s) de l'enfant/des enfants
- {stage} : Nom du stage
- {centre} : Nom du centre
- {periode} : Période du stage
- {semaine} : Semaine du stage
- {telephone} : Téléphone du centre

Cordialement,
L'équipe du centre`)
    setShowCustomEmailModal(true)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'skipped_duplicate':
        return <SkipForward className="h-4 w-4 text-blue-500" />
      case 'batch_completed':
        return <TrendingUp className="h-4 w-4 text-green-600" />
      case 'batch_partial_failure':
        return <TrendingUp className="h-4 w-4 text-orange-600" />
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'success':
        return 'Envoyé'
      case 'failed':
        return 'Échec'
      case 'skipped_duplicate':
        return 'Doublon ignoré'
      case 'batch_completed':
        return 'Lot terminé'
      case 'batch_partial_failure':
        return 'Lot partiel'
      default:
        return 'Inconnu'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatSessionDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  const getFilteredEmailLogs = () => {
    switch (activeTab) {
      case 'pre-session':
        return emailLogs.filter(log => log.type === 'pre_session' || !log.type) // Inclure les anciens logs sans type
      case 'group-messages':
        return emailLogs.filter(log => log.type === 'custom_group')
      case 'payment-reminders':
        return emailLogs.filter(log => log.type === 'payment_reminder')
      case 'evaluation':
        return emailLogs.filter(log => log.type === 'evaluation')
      case 'history':
        return emailLogs
      default:
        return emailLogs
    }
  }

  const tabs = [
    {
      id: 'pre-session' as CommunicationTab,
      name: 'Messages pré-session',
      icon: Calendar,
      description: 'Communications une semaine avant le début des sessions'
    },
    {
      id: 'group-messages' as CommunicationTab,
      name: 'Messages aux groupes',
      icon: MessageSquare,
      description: 'Communications personnalisées aux sessions en cours'
    },
    {
      id: 'whatsapp' as CommunicationTab,
      name: 'Communautés WhatsApp',
      icon: MessageCircle,
      description: 'Inviter les parents à la communauté WhatsApp de leur centre, quelques jours avant la semaine'
    },
    {
      id: 'payment-reminders' as CommunicationTab,
      name: 'Rappels de paiement',
      icon: CreditCard,
      description: 'Rappels automatiques pour les paiements en attente'
    },
    {
      id: 'evaluation' as CommunicationTab,
      name: 'Évaluations',
      icon: Star,
      description: "Envoi du formulaire d'évaluation aux parents à la fin d'une période de plaines"
    },
    {
      id: 'history' as CommunicationTab,
      name: 'Historique',
      icon: Eye,
      description: 'Historique complet de tous les envois'
    },
    {
      id: 'supplies' as CommunicationTab,
      name: 'Matériel à prévoir',
      icon: Package,
      description: 'Gérer la liste du matériel à prévoir par période (affiché dans les emails pré-session)'
    }
  ]

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Communication</h1>
          <p className="text-gray-600">Gestion des communications par email</p>
        </div>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Communication</h1>
        <p className="text-gray-600">Gestion des communications par email</p>
      </div>

      {/* Navigation par onglets */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className={`-ml-0.5 mr-2 h-5 w-5 ${
                    activeTab === tab.id ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`} />
                  {tab.name}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Description de l'onglet actif */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <p className="text-sm text-gray-600">
            {tabs.find(tab => tab.id === activeTab)?.description}
          </p>
        </div>

        {/* Contenu des onglets */}
        <div className="p-6">
          {/* Onglet Messages pré-session */}
          {activeTab === 'pre-session' && <EnvoisPreSession />}

          {/* Onglet Messages aux groupes */}
          {activeTab === 'group-messages' && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Messages aux groupes</h3>
                    <p className="text-sm text-gray-600">
                      Envoyez des messages personnalisés aux parents. Par défaut, seules les sessions en cours cette semaine sont affichées.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      showFilters ? 'bg-blue-600 text-white' : 'bg-gray-600 text-white hover:bg-gray-700'
                    }`}
                  >
                    <Filter className="h-4 w-4" />
                    <span>Filtres</span>
                  </button>
                </div>
              </div>

              {/* Filtres */}
              {showFilters && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Période
                      </label>
                      <select
                        value={sessionFilters.periode}
                        onChange={(e) => setSessionFilters(prev => ({ ...prev, periode: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Toutes les périodes</option>
                        {filterOptions.periodes.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Centre
                      </label>
                      <select
                        value={sessionFilters.center_id}
                        onChange={(e) => setSessionFilters(prev => ({ ...prev, center_id: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Tous les centres</option>
                        {uniqueCenters.map((center) => (
                          <option key={center.id} value={center.id}>
                            {center.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Semaine
                      </label>
                      <select
                        value={sessionFilters.semaine}
                        onChange={(e) => setSessionFilters(prev => ({ ...prev, semaine: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Toutes les semaines</option>
                        {filterOptions.semaines.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-end">
                      <button
                        onClick={resetSessionFilters}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                      >
                        <RotateCcw className="h-4 w-4" />
                        <span>Reset</span>
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 text-sm text-gray-600">
                    <strong>Résultats :</strong> {filteredSessions.length} session{filteredSessions.length !== 1 ? 's' : ''} trouvée{filteredSessions.length !== 1 ? 's' : ''}
                    {!sessionFilters.periode && !sessionFilters.center_id && !sessionFilters.semaine && (
                      <span className="ml-2 text-blue-600">(sessions en cours cette semaine par défaut)</span>
                    )}
                  </div>
                </div>
              )}

              {filteredSessions.length > 0 ? (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Cliquez sur le bouton d'édition pour envoyer un message personnalisé aux parents de la session.
                  </p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {filteredSessions.map(session => {
                      const sessionStatus = getSessionStatus(session)
                      const isCurrentWeek = sessionStatus.status === 'current'
                      
                      return (
                      <div key={session.session_id} className={`border rounded-lg p-6 hover:shadow-md transition-shadow ${
                        isCurrentWeek 
                          ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
                          : sessionStatus.status === 'future'
                            ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'
                            : 'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200'
                      }`}>
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className={`text-lg font-semibold mb-2 flex items-center ${
                                isCurrentWeek ? 'text-green-900' : 
                                sessionStatus.status === 'future' ? 'text-blue-900' : 'text-gray-900'
                              }`}>
                                <Calendar className="h-5 w-5 mr-2" />
                                {session.stage_title}
                              </h3>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${sessionStatus.color}`}>
                                {sessionStatus.label}
                              </span>
                            </div>
                            <div className="space-y-2 text-sm text-gray-700">
                              <div className="flex items-center">
                                <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                                <span>{session.center_name}</span>
                              </div>
                              <div className="flex items-center">
                                <Clock className="h-4 w-4 mr-2 text-gray-500" />
                                <span>{session.session_periode}{session.session_semaine ? ` - ${session.session_semaine}` : ''}</span>
                              </div>
                              <div className="flex items-center">
                                <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                                <span>Du {formatSessionDate(session.session_start_date)} au {formatSessionDate(session.session_end_date)}</span>
                              </div>
                              <div className="flex items-center">
                                <Users className="h-4 w-4 mr-2 text-gray-500" />
                                <span className={`font-medium ${
                                  isCurrentWeek ? 'text-green-700' : 
                                  sessionStatus.status === 'future' ? 'text-blue-700' : 'text-gray-700'
                                }`}>
                                  {session.registrations_count} enfants inscrits
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => openCustomEmailModal(session)}
                            className={`flex items-center justify-center w-12 h-12 text-white rounded-full hover:shadow-xl transition-colors shadow-lg ${
                              isCurrentWeek 
                                ? 'bg-green-600 hover:bg-green-700'
                                : sessionStatus.status === 'future'
                                  ? 'bg-blue-600 hover:bg-blue-700'
                                  : 'bg-gray-600 hover:bg-gray-700'
                            }`}
                            title="Envoyer un email personnalisé"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                        </div>
                        
                        <div className="bg-white bg-opacity-60 rounded-lg p-3 mt-4">
                          <p className="text-xs text-gray-600">
                            <strong>Email personnalisé :</strong> Envoyez un message spécifique aux parents 
                            de cette session. Variables disponibles pour personnaliser le contenu.
                          </p>
                        </div>
                      </div>
                    )})}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune session trouvée</h3>
                  <p>
                    {!sessionFilters.periode && !sessionFilters.center_id && !sessionFilters.semaine
                      ? "Aucune session n'est actuellement en cours cette semaine."
                      : "Aucune session ne correspond aux critères de filtrage sélectionnés."
                    }
                  </p>
                  {(sessionFilters.periode || sessionFilters.center_id || sessionFilters.semaine) && (
                    <button
                      onClick={resetSessionFilters}
                      className="mt-4 text-blue-600 hover:text-blue-800 underline"
                    >
                      Réinitialiser les filtres
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Onglet Rappels de paiement */}
          {activeTab === 'payment-reminders' && (
            <div className="text-center py-12 text-gray-500">
              <CreditCard className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Rappels de paiement</h3>
              <p>Cette fonctionnalité sera développée prochainement.</p>
              <p className="text-sm mt-2">
                Permettra d'envoyer des rappels automatiques aux parents 
                pour les inscriptions en attente de paiement.
              </p>
            </div>
          )}

          {/* Onglet Historique */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Historique complet des envois</h3>
                <p className="text-sm text-gray-600">
                  Tous les emails envoyés depuis l'application, organisés par type et date.
                </p>
              </div>

              <div className="overflow-x-auto">
                {getFilteredEmailLogs().length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Destinataire
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sujet
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statistiques
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Erreur
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getFilteredEmailLogs().map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(log.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              log.type === 'pre_session' ? 'bg-blue-100 text-blue-800' :
                              log.type === 'custom_group' ? 'bg-green-100 text-green-800' :
                              log.type === 'payment_reminder' ? 'bg-orange-100 text-orange-800' :
                              log.type === 'whatsapp_invitation' ? 'bg-emerald-100 text-emerald-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {log.type === 'pre_session' ? 'Pré-session' :
                               log.type === 'custom_group' ? 'Groupe' :
                               log.type === 'payment_reminder' ? 'Paiement' :
                               log.type === 'whatsapp_invitation' ? 'WhatsApp' :
                               log.type === 'evaluation' ? 'Évaluation' :
                               'Ancien'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {log.recipient_email?.startsWith('BATCH_SUMMARY_') ? 
                              <span className="text-gray-500 italic">Résumé groupé</span> : 
                              log.recipient_email
                            }
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {log.subject}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(log.status)}
                              <span className="text-sm text-gray-900">
                                {getStatusText(log.status)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {(log.status === 'batch_completed' || log.status === 'batch_partial_failure') && log.total_recipients_in_batch ? (
                              <div className="space-y-1">
                                <div>Total: {log.total_recipients_in_batch}</div>
                                <div className="text-green-600">✓ {log.successful_sends_in_batch || 0}</div>
                                {(log.failed_sends_in_batch || 0) > 0 && (
                                  <div className="text-red-600">✗ {log.failed_sends_in_batch}</div>
                                )}
                                {(log.skipped_duplicates_in_batch || 0) > 0 && (
                                  <div className="text-blue-600">⏭ {log.skipped_duplicates_in_batch}</div>
                                )}
                              </div>
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-red-600">
                            {log.error_message || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Mail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun email trouvé</h3>
                    <p>Aucun email de ce type n'a été envoyé.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Onglet Communautés WhatsApp */}
          {activeTab === 'whatsapp' && <CommunautesWhatsapp />}

          {/* Onglet Évaluations */}
          {activeTab === 'evaluation' && (
            <EvaluationCampaign />
          )}

          {/* Onglet Matériel à prévoir */}
          {activeTab === 'supplies' && (
            <SuppliesManagement />
          )}
        </div>
      </div>

      {/* Modal d'email personnalisé */}
      {showCustomEmailModal && selectedSessionForCustomEmail && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-6 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                Envoyer un email personnalisé
              </h3>
              <button
                onClick={() => {
                  setShowCustomEmailModal(false)
                  setSelectedSessionForCustomEmail(null)
                  setCustomEmailSubject('')
                  setCustomEmailBody('')
                }}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Informations de la session */}
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h4 className="font-semibold text-blue-900 mb-2">Session sélectionnée :</h4>
              <div className="text-sm text-blue-800">
                <div><strong>Stage :</strong> {selectedSessionForCustomEmail.stage_title}</div>
                <div><strong>Centre :</strong> {selectedSessionForCustomEmail.center_name}</div>
                <div><strong>Période :</strong> {selectedSessionForCustomEmail.session_periode} - {selectedSessionForCustomEmail.session_semaine}</div>
                <div><strong>Dates :</strong> Du {formatSessionDate(selectedSessionForCustomEmail.session_start_date)} au {formatSessionDate(selectedSessionForCustomEmail.session_end_date)}</div>
                <div><strong>Enfants inscrits :</strong> {selectedSessionForCustomEmail.registrations_count}</div>
              </div>
            </div>

            {/* Formulaire d'email */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Objet de l'email *
                </label>
                <input
                  type="text"
                  value={customEmailSubject}
                  onChange={(e) => setCustomEmailSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Objet de votre email..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Corps de l'email *
                </label>
                <textarea
                  value={customEmailBody}
                  onChange={(e) => setCustomEmailBody(e.target.value)}
                  rows={8}
                  placeholder={`Bonjour {nom_parent},

Nous espérons que {enfants} passe(nt) un excellent moment au stage {stage} !

[Votre message personnalisé ici]

Pour toute question, n'hésitez pas à contacter le centre au {telephone}.

Cordialement,
L'équipe du centre`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>

              {/* Aide sur les variables */}
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-semibold text-yellow-900 mb-2">Variables disponibles :</h4>
                <div className="text-sm text-yellow-800 grid grid-cols-2 gap-2">
                  <div><code>{'{nom_parent}'}</code> - Nom du parent</div>
                  <div><code>{'{enfants}'}</code> - Nom(s) de l'enfant/des enfants</div>
                  <div><code>{'{stage}'}</code> - Nom du stage</div>
                  <div><code>{'{centre}'}</code> - Nom du centre</div>
                  <div><code>{'{periode}'}</code> - Période du stage</div>
                  <div><code>{'{semaine}'}</code> - Semaine du stage</div>
                  <div><code>{'{telephone}'}</code> - Téléphone du centre</div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end space-x-3 border-t border-gray-200 pt-4">
              <button
                onClick={() => {
                  setShowCustomEmailModal(false)
                  setSelectedSessionForCustomEmail(null)
                  setCustomEmailSubject('')
                  setCustomEmailBody('')
                }}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={sendCustomEmailForSession}
                disabled={sendingCustomEmail || !customEmailSubject.trim() || !customEmailBody.trim()}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sendingCustomEmail ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Envoi en cours...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Envoyer l'email</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}